import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';

import { LegalAccountTaxationType } from '../../../graphql/schema';
import { CLIENT_TYPE_NAME, TRUST_TYPE_NAME } from '../../../Shared/constants';

type EnvelopeAccount = HouseholdAccountsQuery['household']['accounts'][number];
type Envelope = EnvelopeAccount['envelope'];
type AccountHolder = EnvelopeAccount['accountHolders'][number];

type ManagedAccountType = Extract<
  HouseholdAccountsQuery['household']['accounts'][number],
  { __typename: 'ManagedAccount' }
>;
type PendingActionType = ManagedAccountType['pendingActions'][number]['__typename'];
export const hasPendingAction = (account: EnvelopeAccount, pendingActionType: PendingActionType): boolean => {
  if (account.__typename !== 'ManagedAccount') {
    return false;
  }

  return account.pendingActions.some(pendingAction => {
    if (pendingAction.__typename !== pendingActionType) {
      return false;
    }

    // For PortfolioMigrationPendingAction, also check isFirmDesigned
    if (pendingAction.__typename === 'PortfolioMigrationPendingAction') {
      return pendingAction.isFirmDesigned;
    }

    return true;
  });
};

export const hasRecurringTransactions = (account: EnvelopeAccount): boolean => {
  if (account.__typename === 'ManagedAccount' || account.__typename === 'CashAccount') {
    return account.recurringTransactionConfigs.length > 0;
  }

  return false;
};

export function formatAccountHolders(accountHolders: AccountHolder[]): string {
  return accountHolders
    .map((accountHolder: AccountHolder) => {
      if (accountHolder.__typename === CLIENT_TYPE_NAME) {
        return accountHolder.commonFirstName;
      }

      if (accountHolder.__typename === TRUST_TYPE_NAME && accountHolder.individualGrantor) {
        return accountHolder.individualGrantor.commonFirstName;
      }

      return accountHolder.name;
    })
    .join(', ');
}

export const getUniqueEnvelopesWithPurposeFromAccounts = (accounts: EnvelopeAccount[]) => {
  const envelopesMap = new Map<
    string,
    {
      envelope: Envelope;
      accountHolders: Set<AccountHolder>;
    }
  >();

  accounts.forEach(account => {
    if (account.envelope.purpose) {
      const envelopeId = account.envelope.id;

      if (!envelopesMap.has(envelopeId)) {
        envelopesMap.set(envelopeId, { envelope: account.envelope, accountHolders: new Set() });
      }

      account.accountHolders.forEach(holder => {
        envelopesMap.get(envelopeId)!.accountHolders.add(holder);
      });
    }
  });

  // Convert to separate arrays
  const envelopes = Array.from(envelopesMap.values()).map(({ envelope }) => envelope);
  const accountHoldersMap = new Map<string, AccountHolder[]>();

  // Create a map of envelope ID to account holders
  envelopesMap.forEach(({ accountHolders }, envelopeId) => {
    accountHoldersMap.set(envelopeId, Array.from(accountHolders));
  });

  return { envelopes, accountHoldersMap };
};

export const accountHasSbloc = (account: EnvelopeAccount): boolean => {
  if (account.__typename === 'ManagedAccount') {
    // Check all subAccounts for SBLOC loans
    return account.subAccounts.some(subAccount => subAccount.legalAccount.sblocLoan != null) ?? false;
  }

  if (account.__typename === 'CashAccount' || account.__typename === 'DirectTradingAccount') {
    // Check the account's legalAccount for SBLOC loan
    return account.legalAccount.sblocLoan != null;
  }

  return false;
};

export const accountHasSolo401k = (account: EnvelopeAccount): boolean => {
  if (account.__typename === 'ManagedAccount') {
    return account.subAccounts.some(subAccount => subAccount.legalAccount.soloFourOhOneKPlan != null) ?? false;
  }

  if (account.__typename === 'CashAccount' || account.__typename === 'DirectTradingAccount') {
    return account.legalAccount.soloFourOhOneKPlan != null;
  }

  return false;
};

export const accountCanAddSpouseToSolo = (account: EnvelopeAccount): boolean => {
  if (account.__typename === 'ManagedAccount') {
    return account.subAccounts.some(
      subAccount =>
        subAccount.legalAccount.soloFourOhOneKPlan != null &&
        subAccount.legalAccount.soloFourOhOneKPlan.participants.length < 2
    );
  }

  if (account.__typename === 'CashAccount' || account.__typename === 'DirectTradingAccount') {
    return (
      account.legalAccount.soloFourOhOneKPlan != null && account.legalAccount.soloFourOhOneKPlan.participants.length < 2
    );
  }

  return false;
};

export type ManagedAccountWithSubAccounts = {
  subAccounts: Array<{
    legacySubAccountId: number;
    legalAccount: {
      taxationType: LegalAccountTaxationType;
    };
  }>;
  users: Array<{ id: string }>;
};

const isSingleAccountWithTaxationType = (
  managedAccount: ManagedAccountWithSubAccounts,
  taxationType: LegalAccountTaxationType
): boolean => {
  return (
    managedAccount.subAccounts.length === 1 && managedAccount.subAccounts[0].legalAccount.taxationType === taxationType
  );
};

export const isSingleTraditionalIRAAccount = (managedAccount: ManagedAccountWithSubAccounts | null): boolean => {
  return (
    managedAccount !== null && isSingleAccountWithTaxationType(managedAccount, LegalAccountTaxationType.TraditionalIra)
  );
};

export const isSingleRothIRAAccount = (managedAccount: ManagedAccountWithSubAccounts | null): boolean => {
  return managedAccount !== null && isSingleAccountWithTaxationType(managedAccount, LegalAccountTaxationType.RothIra);
};
